import { useI18n } from "@core/i18n/i18n";
import { localeManager } from "@core/service-container";
import { ProspectCampaigns, ProspectRequestConfig } from "@domain/prospect-request/prospect-request";

import styled from "@emotion/styled";
import { CountryInput } from "@ui/components/inputs/country-input";
import { TextInput } from "@ui/components/inputs/text-input";
import { useCountryConfig } from "@ui/pages/patient-details/hooks/use-country-config";
import { EmailController } from "@ui/pages/prospect/components/email-controller";
import { FormField } from "@ui/pages/prospect/components/form-field";
import { MedicalProfessionController } from "@ui/pages/prospect/components/medical-profession-controller";
import { PhoneNumberController } from "@ui/pages/prospect/components/phone-number-controller";
import { useProspectRequestReferential } from "@ui/pages/prospect/hooks/use-prospect-request-referential";
import { PtInfoFormData } from "@ui/pages/prospect/pt-info-form-data";
import { useMedicalProfessionOptions } from "@ui/pages/referential/use-medical-profession-options";
import { physioMeTabletBreakpoint } from "@ui/style";
import React, { useCallback, useEffect } from "react";
import { Control, Controller, UseFormClearErrors, UseFormResetField } from "react-hook-form";

interface PtInfoFormControlsProps<T extends PtInfoFormData> {
	config: ProspectRequestConfig;
	className?: string;
	control?: Control<T>;
	resetField: UseFormResetField<T>;
	clearErrors: UseFormClearErrors<T>;
	initialCountryCode: string;
	campaign: ProspectCampaigns;
	onPrivacyNoticeUrlChange: (url: string) => void;
}

export function PtInfoControls<T extends PtInfoFormData>({
	config,
	control,
	resetField,
	clearErrors,
	className,
	campaign,
	initialCountryCode,
	onPrivacyNoticeUrlChange,
}: PtInfoFormControlsProps<T>): JSX.Element {
	const { format } = useI18n();

	const ptControl = control as unknown as Control<PtInfoFormData>;
	const resetPtField = resetField as unknown as UseFormResetField<PtInfoFormData>;

	const { referential, selectReferential } = useProspectRequestReferential(config);
	const { countryConfig } = useCountryConfig();

	useEffect(() => {
		onPrivacyNoticeUrlChange(
			(campaign === ProspectCampaigns.DEMO_REQUEST
				? countryConfig?.ptBiogenPrivacyNoticeUrl
				: countryConfig?.patientBiogenPrivacyNoticeUrl) ?? "",
		);
	}, [
		campaign,
		referential,
		onPrivacyNoticeUrlChange,
		countryConfig?.ptBiogenPrivacyNoticeUrl,
		countryConfig?.patientBiogenPrivacyNoticeUrl,
	]);

	const { medicalProfessionOptions } = useMedicalProfessionOptions({
		medicalProfessions: referential.medicalProfessions,
	});

	const fieldRequiredErrorMessage = format("global.form.field.requiredError");

	const switchCountry = useCallback(
		(event: { target: { value: string } }) => {
			const { classList } = document.documentElement;
			if (classList.contains("translated-ltr") || classList.contains("translated-rtl")) {
				return;
			}
			const countryCode = event.target.value;
			selectReferential(countryCode);
			const newLocale = localeManager.resolveLocaleForCountry(countryCode);
			if (newLocale) {
				localeManager.changeLocale(newLocale);
			}
			resetPtField("postalCode");
			resetPtField("medicalProfession");
			clearErrors();
		},
		[clearErrors, resetPtField, selectReferential],
	);

	return (
		<FormContainer className={className}>
			<TwoColumnLayout>
				<Controller
					name="firstName"
					control={ptControl}
					rules={{
						required: fieldRequiredErrorMessage,
					}}
					render={({ field, fieldState }) => (
						<FormField title={format("demoRequest.firstNameField.title")} errorMessage={fieldState.error?.message}>
							<TextInput
								{...field}
								placeholder={format("demoRequest.firstNameField.placeholder")}
								error={fieldState.invalid}
								inputMode="text"
								autoCorrect="off"
								autoCapitalize="none"
							/>
						</FormField>
					)}
				/>
				<Controller
					name="lastName"
					control={ptControl}
					rules={{
						required: fieldRequiredErrorMessage,
					}}
					render={({ field, fieldState }) => (
						<FormField title={format("demoRequest.lastNameField.title")} errorMessage={fieldState.error?.message}>
							<TextInput
								{...field}
								placeholder={format("demoRequest.lastNameField.placeholder")}
								error={fieldState.invalid}
								inputMode="text"
								autoCorrect="off"
								autoCapitalize="none"
							/>
						</FormField>
					)}
				/>
			</TwoColumnLayout>
			<TwoColumnLayout>
				<Controller
					name="countryCode"
					control={ptControl}
					rules={{
						required: fieldRequiredErrorMessage,
						onChange: switchCountry,
					}}
					render={({ field, fieldState }) => (
						<FormField title={format("demoRequest.countryField.title")} errorMessage={fieldState.error?.message}>
							<CountryInput countryCodes={config.supportedCountries} {...field} />
						</FormField>
					)}
				/>
				<Controller
					name="postalCode"
					control={ptControl}
					rules={{
						required: fieldRequiredErrorMessage,
						pattern: {
							value: new RegExp(referential.postalCode.validationRegex),
							message: format("demoRequest.postalCodeField.errorInvalidCodeMessage"),
						},
					}}
					render={({ field, fieldState }) => (
						<FormField title={format("demoRequest.postalCodeField.title")} errorMessage={fieldState.error?.message}>
							<TextInput
								{...field}
								placeholder={referential.postalCode.placeholder}
								error={fieldState.invalid}
								inputMode="text"
								autoCorrect="off"
								autoCapitalize="none"
							/>
						</FormField>
					)}
				/>
			</TwoColumnLayout>

			{campaign === ProspectCampaigns.DEMO_REQUEST ? (
				<>
					<EmailController control={ptControl} isRequired />
					<PhoneNumberController control={ptControl} initialCountryCode={initialCountryCode} />
					<MedicalProfessionController control={ptControl} options={medicalProfessionOptions} isRequired />
				</>
			) : (
				<>
					<PhoneNumberController control={ptControl} initialCountryCode={initialCountryCode} />
					<MedicalProfessionController control={ptControl} options={medicalProfessionOptions} />
				</>
			)}
		</FormContainer>
	);
}

const FormContainer = styled.div`
	display: grid;
	gap: 16px;
`;

const TwoColumnLayout = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 24px;

	@media ${physioMeTabletBreakpoint} {
		grid-template-columns: unset;
	}
`;
